import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import type { FilteredTableList } from '../../../../core/api/schema';
import { injectCatchHttpError } from '../../../../core/errors/catch-http-error.util';
import { SnackBarType } from '../../../../core/models/snack-bar.models';
import type { ITableItem } from '../../../../project/models';
import { selectTable } from '../../../../project/store';
import { PaginationService } from '../../../../core/services/pagination/pagination.service';
import { SnackBarService } from '../../../../core/services/snack-bar/snack-bar.service';
import { ItemPaginationType } from '../../../../core/store/pagination/pagination.actions';
import { DetectUnsavedTableChangesService } from '../../table/services/table/detect-unsaved-table-changes.service';
import type { IFilteredTable, IWrittenFilteredTable } from '../models';
import { FilteredTableApiService } from '../services/filtered-table-api.service';
import { selectActiveFilteredTable } from './filtered-table.reducer';
import {
  cancelFilteredTableRequest,
  createFilteredTable,
  createFilteredTableFail,
  createFilteredTableSuccess,
  deleteFilteredTable,
  deleteFilteredTableFail,
  deleteFilteredTableSuccess,
  getFilteredTable,
  getFilteredTableFail,
  getFilteredTableSuccess,
  setFilteredTable,
  updateFilteredTable,
  updateFilteredTableFail,
  updateFilteredTableSuccess,
} from './filtered-table.actions';

@Injectable()
export class FilteredTableEffects {
  private filteredTableApi = inject(FilteredTableApiService);
  private paginationService = inject(PaginationService);
  private detectUnsavedTableChangesService = inject(DetectUnsavedTableChangesService);
  private snackBarService = inject(SnackBarService);

  private readonly actions$ = inject<Actions>(Actions);
  private store = inject<Store>(Store);
  private readonly catchHttpError = injectCatchHttpError();

  private toListItem(written: IWrittenFilteredTable, table: ITableItem): FilteredTableList {
    return {
      id: written.id,
      url: written.url,
      created_by: written.created_by,
      modified_by: written.modified_by,
      name: written.name,
      read_only: written.read_only,
      table: { id: table.id, name: table.name, closed_by: table.closed_by },
    };
  }

  /**
   * The write endpoints name their table by uri. Both candidates are read when the action is
   * dispatched, not when it answers: the manage dialog closes — clearing them — before the write returns.
   */
  private expand(written: IWrittenFilteredTable, ...candidates: (ITableItem | undefined)[]): IFilteredTable | undefined {
    const table = candidates.find(candidate => candidate?.url === written.table);
    return table && { ...written, table, has_custom_docxtemplate: table.has_custom_docxtemplate };
  }

  createFilteredTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createFilteredTable),
      map(action => action.params),
      withLatestFrom(this.store.select(selectTable), this.store.select(selectActiveFilteredTable)),
      switchMap(([params, table, active]) =>
        this.filteredTableApi.createFilteredTable(params).pipe(
          switchMap(written => {
            const filteredTable = this.expand(written, table, active?.table);
            if (filteredTable) {
              this.paginationService.addItem(this.toListItem(written, filteredTable.table), ItemPaginationType.FilteredTablesDropdown);
            }
            this.snackBarService.show(SnackBarType.Success, 'FILTERED_TABLE.VIEW_CHANGED');
            return [
              createFilteredTableSuccess(),
              filteredTable ? setFilteredTable({ filteredTable }) : getFilteredTable({ id: written.id }),
            ];
          }),
          tap(() => {
            this.detectUnsavedTableChangesService.tableColumnsSaved$.emit();
          }),
          this.catchHttpError(() => createFilteredTableFail()),
        ),
      ),
    ),
  );

  updateFilteredTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateFilteredTable),
      withLatestFrom(this.store.select(selectTable), this.store.select(selectActiveFilteredTable)),
      switchMap(([action, table, active]) =>
        this.filteredTableApi.updateFilteredTable(action.id, action.updateFilteredTableParams).pipe(
          switchMap(written => {
            const filteredTable = this.expand(written, table, active?.table);
            this.paginationService.updateItem<FilteredTableList>(
              { id: action.id, patch: { name: written.name, read_only: written.read_only } },
              ItemPaginationType.FilteredTablesDropdown,
            );
            this.snackBarService.show(SnackBarType.Success, 'FILTERED_TABLE.VIEW_CHANGED');
            return [
              updateFilteredTableSuccess(),
              filteredTable ? setFilteredTable({ filteredTable }) : getFilteredTable({ id: written.id }),
            ];
          }),
          tap(() => {
            this.detectUnsavedTableChangesService.tableColumnsSaved$.emit();
          }),
          this.catchHttpError(() => updateFilteredTableFail()),
        ),
      ),
    ),
  );

  deleteFilteredTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteFilteredTable),
      switchMap(action =>
        this.filteredTableApi.deleteFilteredTable(action.id).pipe(
          switchMap(() => {
            this.paginationService.deleteItem({ id: action.id }, ItemPaginationType.FilteredTablesDropdown);
            return [deleteFilteredTableSuccess(), setFilteredTable({ filteredTable: undefined })];
          }),
          this.catchHttpError(() => deleteFilteredTableFail()),
        ),
      ),
    ),
  );

  getFilteredTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getFilteredTable),
      switchMap(action =>
        this.filteredTableApi.getFilteredTable(action.id).pipe(
          takeUntil(this.actions$.pipe(ofType(cancelFilteredTableRequest))),
          switchMap(filteredTable => [getFilteredTableSuccess({ filteredTable })]),
          tap(() => {
            this.detectUnsavedTableChangesService.tableColumnsSaved$.emit();
          }),
          this.catchHttpError(() => getFilteredTableFail()),
        ),
      ),
    ),
  );
}
